import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { Pillar3aProductConstraintsService } from "src/app/shared/store/quote/pillar-3a/domain/product-constraints.service";
import { TermLifeInsuranceProductConstraintsService } from "src/app/shared/store/quote/term-life-insurance/domain/product-constraints.service";
import { UnreachableCaseError } from "ts-essentials";
import { Product } from "../shared/product";

@Injectable({ providedIn: "root" })
export class ProductConstraintsFactory {
  constructor(
    private readonly _pillar3aConstraintsService: Pillar3aProductConstraintsService,
    private readonly _termLifeInsuranceConstraintsService: TermLifeInsuranceProductConstraintsService,
  ) {}

  get(product: Product) {
    let productConstraintsService: ProductConstraintsService;

    switch (product) {
      case Product.TERM_LIFE_INSURANCE_CONSTANT:
        productConstraintsService = this._termLifeInsuranceConstraintsService;
        break;
      case Product.PILLAR_3A_CONSTANT:
        productConstraintsService = this._pillar3aConstraintsService;
        break;
      default:
        throw new UnreachableCaseError(product);
    }

    return productConstraintsService;
  }
}

export interface ProductConstraintsService {
  get(): Observable<ProductConstraints>;

  getInsuredSumConstraints(): Observable<InsuredSumConstraints>;

  getEntryAgeConstraints(): Observable<EntryAgeConstraints>;

  getBmiConstraints(): Observable<BMIConstraints>;

  getYearlyPremiumConstraints(): Observable<YearlyPremiumConstraints>;

  getMaximumCoverageAge(): Observable<number | undefined>;
}

/**
 * This interface contains all constraints for a specific product.
 * A constraint is, as an example, the maximum age for a term life insurance product.
 */
export interface ProductConstraints {
  insuredSum: InsuredSumConstraints | undefined;
  entryAge: EntryAgeConstraints | undefined;
  bmi: BMIConstraints | undefined;
  yearlyPremium: YearlyPremiumConstraints | undefined;
  maximumCoverageAge: number | undefined;
}

export interface BMIConstraints {
  minHeightInCm: number | undefined;
  maxHeightInCm: number | undefined;
  minWeightInKg: number | undefined;
  maxWeightInKg: number | undefined;
  min: number | undefined;
  max: number | undefined;
}

export interface InsuredSumConstraints {
  min: number | undefined;
  max: number | undefined;
  maxWithoutMedicalCheckup: number | undefined;
}

export interface EntryAgeConstraints {
  min: number | undefined;
  max: number | undefined;
}

export interface YearlyPremiumConstraints {
  min: number | undefined;
  max: number | undefined;
}

// Term Life Insurance
export type TermLifeInsuranceProductConstraints = Pick<
  ProductConstraints,
  "insuredSum" | "entryAge" | "maximumCoverageAge" | "bmi"
>;

// Pillar 3a
export type Pillar3aProductConstraints = Pick<
  ProductConstraints,
  "yearlyPremium" | "entryAge"
>;
